import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { UserLookupResult } from './useUserLookup';
import { searchFakePlayers } from '@/lib/fake-leaderboard-generator';

interface SearchFilters {
  minElo: number;
  maxElo: number;
}

export interface PlayerSearchResult extends UserLookupResult {
  isFake?: boolean;
  isPrivate?: boolean;
}

export const usePlayerSearch = () => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [realResults, setRealResults] = useState<UserLookupResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({ minElo: 0, maxElo: 3000 });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  // Perform search when debounced query changes
  useEffect(() => {
    const searchPlayers = async () => {
      if (!debouncedQuery.trim()) {
        setRealResults([]);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, username, display_name, avatar_type, avatar_config, avatar_url, elo_rating, games_played, games_won')
          .or(`username.ilike.%${debouncedQuery}%,display_name.ilike.%${debouncedQuery}%`)
          .gte('elo_rating', filters.minElo)
          .lte('elo_rating', filters.maxElo)
          .order('elo_rating', { ascending: false })
          .limit(50);

        if (error) throw error;
        setRealResults(data || []);
      } catch (error) {
        console.error('Error searching players:', error);
        setRealResults([]);
      } finally {
        setLoading(false);
      }
    };

    searchPlayers();
  }, [debouncedQuery, filters]);

  // Merge real and fake players
  const results: PlayerSearchResult[] = useMemo(() => {
    if (!debouncedQuery.trim()) return [];

    // Get matching fake players
    const fakePlayers = searchFakePlayers(debouncedQuery, filters.minElo, filters.maxElo);
    
    // Convert fake players to PlayerSearchResult format
    const fakeResults: PlayerSearchResult[] = fakePlayers.map(fp => ({
      id: fp.id,
      username: fp.username,
      display_name: fp.username,
      avatar_type: null,
      avatar_config: null,
      avatar_url: null,
      elo_rating: fp.elo_rating,
      games_played: fp.games_played,
      games_won: fp.games_won,
      isFake: true,
      isPrivate: fp.isPrivate
    }));

    // Merge real (marked as not fake) and fake players
    const realWithFlag: PlayerSearchResult[] = realResults.map(r => ({ ...r, isFake: false }));
    
    // Combine, sort by ELO, and limit
    return [...realWithFlag, ...fakeResults]
      .sort((a, b) => b.elo_rating - a.elo_rating)
      .slice(0, 50);
  }, [debouncedQuery, realResults, filters]);

  return {
    query,
    setQuery,
    results,
    loading,
    filters,
    setFilters
  };
};
